import React, {
  useEffect, useRef, useCallback,
} from 'react';
import { fromJS } from 'immutable';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { combineReducers } from 'redux-immutable';
import { OneFetchyeProvider, OneCache } from 'fetchye-one-app';
import { useDispatch, useSelector } from 'react-redux';
import { IntlProvider, FormattedMessage } from 'react-intl';
import {
  addErrorToReport,
  getCookies,
  loadLanguagePack,
  setErrorReporterSessionData,
} from '@americanexpress/one-app-ducks';
import PropTypes from 'prop-types';
import { ProgressCircle } from '@americanexpress/dls-react';
import { Helmet } from 'react-helmet';
import DataManager from '@americanexpress/data-manager';
import SharedScript, { Adobe } from '@americanexpress/shared-scripts';
import { DDLRegister } from '@americanexpress/react-ddl';
import { useRouter } from '@americanexpress/one-app-router';
import { useReactlytics } from '@americanexpress/reactlytics';
import { StatsigProvider, useStatsig } from '@americanexpress/react-statsig-wrapper';
import {
  AmexDDLRegister,
  getAmexDataManagerConfig,
} from '@americanexpress/data-manager-utils';
import { FallbackRender } from '@americanexpress/arcade-core';

import '@americanexpress/arcade-core/index.css';

import { MODULE_NAME } from '../constants';
import reducer, { MERGE_VALUES, SET_IS_MOBILE } from '../ducks';
import childRoutes from '../childRoutes';
import { checkIsMobile } from '../utils/checkIsMobileUtils';

const StatsigExperimentSync = ({ children }) => {
  const dispatch = useDispatch();
  const hasReportedStatsigErrorRef = useRef(false);
  const {
    isReady,
    error,
    statsigClient,
    user,
  } = useStatsig();
  const experiment = isReady && statsigClient ? statsigClient.getExperiment('arcade') : null;
  const variant = experiment?.groupName || null;
  const experimentName = experiment?.name || null;
  const campaigns = experimentName && variant ? [{ id: experimentName, variant }] : [];

  useEffect(() => {
    if (error) {
      if (!hasReportedStatsigErrorRef.current) {
        dispatch(addErrorToReport(error, {
          collectionMethod: 'statsigInitialization - Exception',
          moduleID: 'arcade-root',
        }));
        hasReportedStatsigErrorRef.current = true;
      }

      dispatch({
        type: MERGE_VALUES,
        payload: fromJS({
          isTilesDispersed: false,
          isExperimentReady: true,
          userID: user?.userID,
        }),
      });
      return;
    }

    hasReportedStatsigErrorRef.current = false;

    if (!isReady) {
      return;
    }

    if (!experiment || variant === null) {
      dispatch({
        type: MERGE_VALUES,
        payload: fromJS({
          isTilesDispersed: false,
          isExperimentReady: true,
          userID: user?.userID,
        }),
      });
      return;
    }

    const isTestGroup = variant.toLowerCase() === 'test';
    dispatch({
      type: MERGE_VALUES,
      payload: fromJS({
        isTilesDispersed: isTestGroup,
        isExperimentReady: true,
        userID: user?.userID,
      }),
    });
  }, [dispatch, error, experiment, isReady, variant, user]);

  return (
    <>
      <DDLRegister name="campaigns" value={campaigns} />
      {children}
    </>
  );
};

StatsigExperimentSync.propTypes = {
  children: PropTypes.element,
};

const ArcadeRoot = ({ languageData, locale, children }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => getCookies(state).blueboxpublic || '');
  // Uncomment Hardcoded userId for local
  // const userId = 1;
  const userStatus = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'userStatus']));
  const akamaiTenantUrl = useSelector((state) => state.getIn(['config', 'akamaiTenantUrl']));
  const environmentTier = useSelector((state) => state.getIn(['config', 'environmentTier'])) || 'production';
  const prevIsMobile = useRef(null);
  const shouldInitializeStatsig = Boolean(userId) && userStatus && userStatus !== 'unauthorized';

  const pollUAChange = useCallback(() => {
    // devTools takes time to update the user agent
    setTimeout(() => {
      const isMobile = checkIsMobile();
      const isMobileChanged = prevIsMobile.current !== isMobile;
      /* istanbul ignore next */
      if (isMobileChanged) {
        prevIsMobile.current = isMobile;
        dispatch({ type: SET_IS_MOBILE, payload: isMobile });
      }
    }, 250); // Allow time for devTools to update the user agent
  }, [dispatch]);

  useEffect(() => {
    // Initial evaluation of mobile state
    pollUAChange();
    // Add resize listener to evaluate mobile state on window resize
    window.addEventListener('resize', pollUAChange);
    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('resize', pollUAChange);
    };
  }, [pollUAChange]);

  useEffect(() => {
    dispatch(setErrorReporterSessionData({
      userId,
    }));
  }, []);

  const {
    onTrackCall,
    ddlProviderProps,
    reactlyticsProps,
  } = getAmexDataManagerConfig({
    initialDDL: {
      historyLimit: 70,
    },
  });

  if (Object.entries(languageData).length === 0) {
    return <ProgressCircle />;
  }

  return (
    <IntlProvider locale={locale} messages={languageData}>
      <FormattedMessage id="arcade.title">
        {(title) => (
          <Helmet
            htmlAttributes={{ lang: locale }}
            title={title[0]}
            link={[
              {
                rel: 'canonical',
                href: 'https://www.americanexpress.com/en-us/web3/passport/',
              },
              {
                rel: 'stylesheet',
                href: 'https://www.aexp-static.com/cdaas/one/statics/@americanexpress/dls/6.21.5/package/dist/6.21.5/styles/dls.min.css',
              },
              { rel: 'icon', href: 'https://www.americanexpress.com/favicon.ico' },
              { rel: 'preconnect', href: 'https://icm.aexp-static.com' },
            ]}
            meta={[
              { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
              { charset: 'utf-8' },
              { name: 'viewport', content: 'width=device-width, initial-scale=1' },
              { name: 'theme-color', content: '#006FCF' },
              { name: 'description', content: 'Collect digital stamps as you travel the world' },
              { property: 'og:title', content: 'Amex Passport' },
              { property: 'og:description', content: 'Collect digital stamps as you travel the world' },
              { property: 'og:image', content: 'https://icm.aexp-static.com/Internet/IMDC/arcade/Images/passport-og-image.png' },
              { property: 'og:url', content: 'https://www.americanexpress.com/en-us/web3/passport/' },
              { property: 'og:type', content: 'website' },
              { property: 'og:site_name', content: 'Amex Passport' },
              { name: 'twitter:card', content: 'summary_large_image' },
              { name: 'twitter:title', content: 'Amex Passport' },
              { name: 'twitter:description', content: 'Collect digital stamps as you travel the world' },
              { name: 'twitter:image', content: 'https://icm.aexp-static.com/Internet/IMDC/arcade/Images/passport-og-image.png' },
              { name: 'twitter:site', content: '@AmericanExpress' },
              { name: 'twitter:creator', content: '@AmericanExpress' },
              { name: 'robots', content: akamaiTenantUrl === 'https://www.americanexpress.com/en-us/web3/passport' ? 'index, follow' : 'noindex, nofollow' },
            ]}
          />
        )}
      </FormattedMessage>
      <DataManager
        onTrackCall={onTrackCall}
        ddlProviderProps={ddlProviderProps}
        reactlyticsProps={reactlyticsProps}
      >
        <AmexDDLRegister disableRegisterList={{ view: true }} />
        <DDLRegister name="user.authenticated" value={false} />
        <Adobe version="0.1.0" />
        <SharedScript name="one-identity-session" version="^1.0.0" windowCheck="AmexSession" />
        <SharedScript
          name="one-stream-data-handler"
          windowCheck="oneStreamData"
          version="^0.1.2"
        />
        <OneFetchyeProvider>
          {shouldInitializeStatsig ? (
            <StatsigProvider
              clientKey="client-iG9K8728fsFuiDjewoiKMor2VNBt9jxlmHPRkBYT4u4"
              options={{
                environment: { tier: environmentTier },
              }}
              identifierType="blueboxpublic"
            >
              <StatsigExperimentSync>
                <div
                  className="width-full height-full"
                  data-testid="arcadeRoot"
                  data-module-name={MODULE_NAME}
                >
                  {children}
                </div>
              </StatsigExperimentSync>
            </StatsigProvider>
          ) : (
            <div
              className="width-full height-full"
              data-testid="arcadeRoot"
              data-module-name={MODULE_NAME}
            >
              {children}
            </div>
          )}
        </OneFetchyeProvider>
      </DataManager>
    </IntlProvider>
  );
};

export const TestableArcadeRoot = ArcadeRoot;

ArcadeRoot.propTypes = {
  languageData: PropTypes.shape({}).isRequired, // no need to restate all the keys in the lang pack
  locale: PropTypes.string.isRequired,
  children: PropTypes.element,
};

// Read about childRoutes:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/Routing.md#childroutes
ArcadeRoot.childRoutes = childRoutes;

// Read about appConfig:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/App-Configuration.md
/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line n/global-require -- need for server-side rendering
  ArcadeRoot.appConfig = require('../appConfig').default;
}

export const loadModuleData = ({ store: { dispatch } }) => dispatch(loadLanguagePack(MODULE_NAME, { fallbackLocale: 'en-US' }));

ArcadeRoot.holocron = {
  name: MODULE_NAME,
  reducer: combineReducers({
    common: reducer,
    fetchye: OneCache().reducer,
  }),
  loadModuleData,
};

/* istanbul ignore next */
const CustomErrorView = () => {
  const history = useRouter();
  const { track } = useReactlytics();
  const isNative = global.BROWSER && sessionStorage.getItem('isNative') === 'true';

  const handleBack = () => {
    track({ event: { identifier: 'axp-passport-stamp-error', element: 'back-button', type: 'click' } });
    history.goBack();
  };

  return (
    <>
      <Helmet
        link={[
          {
            rel: 'stylesheet',
            href: 'https://www.aexp-static.com/cdaas/one/statics/@americanexpress/dls/6.21.5/package/dist/6.21.5/styles/dls.min.css',
          },
        ]}
      />
      <FallbackRender
        onBackBtnClick={handleBack}
        isNative={isNative}
        isFullScreen={true}
        description="We're currently facing some technical difficulties. Please try again."
      />
    </>
  );
};

/* istanbul ignore next */
export default oneAppModuleWrapper(MODULE_NAME, {
  fallbackRender: () => <CustomErrorView />,
})(ArcadeRoot);
